// @flow

import React, {
  Fragment,
  type Node,
  type ComponentType,
  type Component,
} from 'react';
import findIndex from 'lodash/findIndex';
import {browserHistory} from 'react-router';

import {
  type ModelSubscribedComponentPropsType,
  type ModelChangeMessageType,
} from 'app/subscriptions';

import {
  DocSidebarPagePanels,
  StyledFooterDIV,
} from 'app/general/structures/DocSidebarPagePanels';
import  {
  GenericAttachedItemsSidebarItem,
  type Props as ItemProps,
} from './GenericAttachedItemsSidebarItem';
import {SearchInputField} from 'app/general/widgets/SearchInputField';
import {StyledListDIV} from 'app/components/general/VersionsSidebar/VersionsSidebar';
import type {ResponseType} from 'app/utils/redux_v2';
import StateButton from 'app/general/widgets/StateButton';
import {gt} from 'app/utils/i18n';
import {SweetLoadingBar} from 'app/general/sweet-loading-bar';

export type GenericSidebarItemType = {
  +name: string,
  +url: string,
  +html_url?: string,
};

type OwnProps = {
  title: Node,
  showFilterField: boolean,
  onClose: () => void,

  items: Array<GenericSidebarItemType>,
  loadItems: () => Promise<any>,
  handleModelChange?: (
    ModelChangeMessageType,
    GenericAttachedItemsSidebar
  ) => any,
  itemNameProcessor: (GenericSidebarItemType) => string | Component<'span'>,
  ItemComponent: ComponentType<any>,
  itemComponentProps?:
    | $Shape<ItemProps>
    | ((GenericSidebarItemType, boolean) => $Shape<ItemProps>),

  onRemove: (any) => Promise<any>,

  canCreate: boolean,
  createURL: string,
  createLabel: string,

  addTooltipText?: string,
  disabledTooltipText?: string,
  onSearch: (string) => Promise<ResponseType<GenericSidebarItemType>>,
  onLoadMore: (
    ResponseType<GenericSidebarItemType>,
    string
  ) => Promise<ResponseType<GenericSidebarItemType>>,
  searchPlaceholder: string,
  onAttach: (any) => Promise<any>,
  emptyResultsComponents: Node,
};

type Props = ModelSubscribedComponentPropsType & OwnProps;

type State = {
  loading: boolean,
  filterQuery: string,
  searchReturnItems: ResponseType<GenericSidebarItemType>,
};

class GenericAttachedItemsSidebar extends React.PureComponent<Props, State> {
  state = {
    loading: true,
    filterQuery: '',
    searchReturnItems: {
      next: null,
      previous: null,
      count: 0,
      num_pages: 1,
      results: [],
    },
  };

  static defaultProps = {
    ItemComponent: GenericAttachedItemsSidebarItem,
  };

  constructor(props: Props) {
    super(props);
    if (this.props.onModelChange != null) {
      this.props.onModelChange(this.handleModelChange);
    }
  }

  setLoaded = (items: any): any => {
    this.setState({loading: false});
    return items;
  };

  componentDidMount() {
    this.loadItems();
  }

  loadItems = (merge = true, skipNext = false) => {
    const {loadItems} = this.props;
    const {filterQuery} = this.state;
    this.setState({loading: true});

    return Promise.all([
      loadItems(),
      this.search(filterQuery, merge, skipNext),
    ]).then(this.setLoaded, this.setLoaded);
  };

  handleModelChange = (message: ModelChangeMessageType) => {
    if (this.props.handleModelChange != null) {
      this.props.handleModelChange(message, this);
    }
  };

  getItemProps(item: GenericSidebarItemType, attached: boolean) {
    const {itemComponentProps} = this.props;

    if (itemComponentProps == null) {
      return {};
    }

    return typeof itemComponentProps === 'function'
      ? itemComponentProps(item, attached)
      : itemComponentProps;
  }

  handleAttach = (newItem: any) => {
    const {onAttach} = this.props;
    if (onAttach != null) {
      this.setState({loading: true});
      return onAttach(newItem).then(() => this.loadItems(true, true));
    }
    throw 'Missing onAttach prop';
  };

  handleRemove = (item: GenericSidebarItemType) => {
    const {onRemove} = this.props;
    if (onRemove != null) {
      this.setState({loading: true});
      return onRemove(item).then(() => this.loadItems(true, true));
    }
    throw 'Missing onRemove prop';
  };

  getAvailableItems() {
    const selectedItems = this.props.items.map((item) => item.url);

    return this.state.searchReturnItems.results.filter(
      (item) => selectedItems.indexOf(item.url) === -1
    );
  }

  search = (filterQuery: string, merge: boolean = false, skipNext = false) => {
    const {onSearch} = this.props;
    this.setState({
      loading: true,
      filterQuery,
    });

    if (onSearch != null) {
      return onSearch(filterQuery).then((response) =>
        this.setState({
          loading: false,
          searchReturnItems: merge
            ? this.mergeResponseOver(response, skipNext)
            : response,
        })
      );
    }
  };

  mergeResponseOver(
    response: ResponseType<GenericSidebarItemType>,
    skipNext: boolean
  ) {
    const {searchReturnItems} = this.state;

    let results = searchReturnItems.results.slice();

    response.results.map((item) => {
      const index = findIndex(results, {url: item.url});
      index >= 0 ? results.splice(index, 1, item) : results.push(item);
    });

    return {
      ...searchReturnItems,
      ...response,
      next: skipNext ? searchReturnItems.next : response.next,
      results,
    };
  }

  loadMore = () => {
    const {searchReturnItems, filterQuery} = this.state;
    this.setState({loading: true});

    return this.props
      .onLoadMore(searchReturnItems, filterQuery)
      .then((response) =>
        this.setState({
          loading: false,
          searchReturnItems: this.mergeResponseOver(response, false),
        })
      );
  };

  render() {
    const {
      title,
      items,
      onClose,
      //eslint-disable-next-line no-unused-vars
      onRemove,
      //eslint-disable-next-line no-unused-vars
      onSearch,
      searchPlaceholder,
      ItemComponent,
      itemNameProcessor,
      createLabel,
      canCreate,
      createURL,
      showFilterField,
      emptyResultsComponents,
    } = this.props;

    const {
      filterQuery,
      loading,
      searchReturnItems: {next},
    } = this.state;
    const availableItems = this.getAvailableItems();

    return (
      <DocSidebarPagePanels
        title={title}
        additionalHeader={
          <Fragment>
            <div className="position-relative">
              <SweetLoadingBar loading={loading ? 1 : 0} />
            </div>
            {showFilterField ? (
              <SearchInputField
                className="form-control-sm"
                initialValue=""
                placeholder={searchPlaceholder}
                onChange={this.search}
              />
            ) : null}
          </Fragment>
        }
        onClose={onClose}
        footer={
          canCreate === true ? (
            <StyledFooterDIV>
              <button
                className="btn btn-secondary btn-sm btn-block"
                onClick={() =>
                  browserHistory.push(`${createURL}?name=${filterQuery}`)
                }
              >
                <i className="fa fa-plus-circle" /> {createLabel}
              </button>
            </StyledFooterDIV>
          ) : null
        }
      >
        {availableItems.length === 0 && items.length === 0 && !loading ? (
          emptyResultsComponents
        ) : (
          <StyledListDIV>
            <ul className="list-group">
              {items.map((item) => (
                <ItemComponent
                  key={item.url}
                  item={item}
                  attached={true}
                  nameProcessor={itemNameProcessor}
                  onRemove={this.handleRemove}
                  {...this.getItemProps(item, true)}
                />
              ))}
              {availableItems.map((item) => (
                <ItemComponent
                  key={item.url}
                  item={item}
                  attached={false}
                  nameProcessor={itemNameProcessor}
                  onSelect={this.handleAttach}
                  {...this.getItemProps(item, false)}
                />
              ))}
              {availableItems.length > 0 && next != null ? (
                <div className="text-center my-2 mx-4">
                  <StateButton
                    className="btn btn-outline-primary btn-sm btn-block"
                    busyText={gt('Loading...')}
                    onClick={this.loadMore}
                  >
                    {gt('Load more')}
                  </StateButton>
                </div>
              ) : null}
            </ul>
          </StyledListDIV>
        )}
      </DocSidebarPagePanels>
    );
  }
}

export default GenericAttachedItemsSidebar;
